import type { OnboardingStepDeps } from '@/composables/useOnboarding';
import i18n from '@/i18n';
import { createActionStep, createInfoStep, createInputStep } from '@/onboarding/stepStyles';
import type { StepOptions } from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { nextTick } from 'vue';
import type { Composer } from 'vue-i18n';

const t = (key: string, params: Record<string, unknown> = {}) => (i18n.global as unknown as Composer).t(key, params);

export function getProfileAndTeamSteps({ tour, router, waitForElement }: OnboardingStepDeps): StepOptions[] {
  return [
    // 18.1 — Transition vers profil utilisateur
    createActionStep(
      {
        id: 'page-profile-transition',
        title: t('onboarding.chapters.profileAndTeam.steps.page-profile-transition.title'),
        text: t('onboarding.chapters.profileAndTeam.steps.page-profile-transition.text'),
        attachTo: { element: '#onboarding-profile-button', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '#onboarding-profile-button', event: 'click' },
        beforeShowPromise: async () => {
          if (router.currentRoute.value.path.includes('/profile')) {
            console.log("[Onboarding] Retour à la page d'accueil...");
            await router.push('/');
            await nextTick();
          }

          await waitForElement('#onboarding-profile-button', 10000);
          await nextTick();
        },
      },
      {
        placement: 'left',
      },
    ),
    // 18.2 — Profil utilisateur
    createInfoStep({
      id: 'user-profile',
      title: t('onboarding.chapters.profileAndTeam.steps.user-profile.title'),
      text: t('onboarding.chapters.profileAndTeam.steps.user-profile.text'),
      attachTo: { element: '.onboarding-user-profil-link', on: 'bottom' },
      canClickTarget: false,
      beforeShowPromise: async () => {
        await waitForElement('.onboarding-user-profil-link', 10000);
        await nextTick();
      },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        { text: t('onboarding.common.next'), action: () => tour.value?.next() },
      ],
    }),
    //19 - Profil restaurant
    createInfoStep({
      id: 'restaurant-profile',
      title: t('onboarding.chapters.profileAndTeam.steps.restaurant-profile.title'),
      text: t('onboarding.chapters.profileAndTeam.steps.restaurant-profile.text'),
      attachTo: { element: '.onboarding-restaurant-profil-link', on: 'bottom' },
      canClickTarget: false,
      beforeShowPromise: async () => {
        await waitForElement('.onboarding-restaurant-profil-link', 10000);
        await nextTick();
      },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        { text: t('onboarding.common.next'), action: () => tour.value?.next() },
      ],
    }),
    // 20 - Profil Équipe
    createActionStep(
      {
        id: 'team-profile',
        title: t('onboarding.chapters.profileAndTeam.steps.team-profile.title'),
        text: t('onboarding.chapters.profileAndTeam.steps.team-profile.text'),
        attachTo: { element: '.onboarding-team-profil-link', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-team-profil-link', event: 'click' },
        beforeShowPromise: async () => {
          if (!router.currentRoute.value.path.endsWith('/profile')) {
            console.log('[Onboarding] Retour à /profile...');
            await router.push({ path: '/profile' });
            await nextTick();
          }

          await waitForElement('.onboarding-team-profil-link', 10000);
          await nextTick();
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    // 20.1 Ajout d'un employé
    createActionStep(
      {
        id: 'add-team-member',
        title: t('onboarding.chapters.profileAndTeam.steps.add-team-member.title'),
        text: t('onboarding.chapters.profileAndTeam.steps.add-team-member.text'),
        attachTo: { element: '.onboarding-add-team-member-button', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-add-team-member-button', event: 'click' },
        beforeShowPromise: async () => {
          if (router.currentRoute.value.path !== '/profile/team') {
            console.log('[Onboarding] Retour à /profile/team...');
            await router.push({ path: '/profile/team' });
            await nextTick();
          }
          await waitForElement('.onboarding-add-team-member-button', 10000);
          await nextTick();
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    // 20.2 Formulaire ajout employé
    createInputStep(
      {
        id: 'team-member-form',
        title: t('onboarding.chapters.profileAndTeam.steps.team-member-form.title'),
        text: t('onboarding.chapters.profileAndTeam.steps.team-member-form.text', {
          dynamicText: `<strong>${t('onboarding.bold.chapter_7.save')}</strong>`,
        }),
        attachTo: { element: '.onboarding-add-employee-view', on: 'top' },
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-add-employee-confirm', event: 'click' },
        beforeShowPromise: async () => {
          await waitForElement('.onboarding-add-employee-view', 10000);
          await nextTick();
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    // 20.3 Dialog de confirmation
    createActionStep(
      {
        id: 'team-member-confirmation-dialog',
        title: t('onboarding.chapters.profileAndTeam.steps.team-member-confirmation-dialog.title'),
        text: t('onboarding.chapters.profileAndTeam.steps.team-member-confirmation-dialog.text', {
          dynamicText: `<strong>${t('onboarding.bold.chapter_7.no-email-confirm')}</strong>`,
        }),
        attachTo: { element: '.onboarding-add-employee-dialog', on: 'top' },
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-add-employee-dialog-confirm', event: 'click' },
        beforeShowPromise: async () => {
          await waitForElement('.onboarding-add-employee-dialog', 10000);
          await nextTick();
        },
      },
      {
        placement: 'top',
      },
    ),
    // 20.4 Explication après ajout employé
    createInfoStep({
      id: 'team-member-added-explanation',
      title: t('onboarding.chapters.profileAndTeam.steps.team-member-added-explanation.title'),
      text: t('onboarding.chapters.profileAndTeam.steps.team-member-added-explanation.text'),
      canClickTarget: false,
      beforeShowPromise: async () => {
        if (router.currentRoute.value.path !== '/profile/team') {
          await router.push({ path: '/profile/team' });
          await nextTick();
        }
      },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        {
          text: t('onboarding.common.next'),
          action: () => tour.value?.next(),
        },
      ],
    }),
  ];
}
